import {
  EnvironmentProviders,
  ErrorHandler,
  Provider,
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import { I18nPluralPipe } from '@angular/common';
import {
  BdCurrentUserService,
  BdDash0TrackingService,
  BdHubspotTrackingService,
  BdUserErrorHandler,
  BdUserZendeskService,
  TRACKING_SERVICE,
} from '@bindoc-projects/user-lib/data';
import { BdUserCurrentUserModule } from '@bindoc-projects/user-lib/ui';
import { BdPageTitleService } from '@bindoc-projects/util-lib/data';
import { BdVersionCheckService } from './version-check.service';
import { BD_DIALOG_SERVICE_PROVIDER } from '@bindoc-projects/ui-lib/dialog/ui';

/**
 * Root providers for the standard MSAL/B2C app profile (formerly
 * `BdAppStarterModule`).
 *
 * The environment initializer replaces the old NgModule constructor: it runs
 * when the root environment injector is created, i.e. at the same point in the
 * bootstrap sequence as before.
 */
export function provideAppStarter(
  securityProviders: Array<Provider | EnvironmentProviders>,
): Array<Provider | EnvironmentProviders> {
  return [
    ...securityProviders,
    importProvidersFrom(BdUserCurrentUserModule),
    // Not `providedIn: 'root'`: the pipe class carries no provider of its own, and
    // `BdPostalCodeService` (maps-lib) injects it from the root injector rather
    // than from a component's `providers`.
    I18nPluralPipe,
    {
      provide: TRACKING_SERVICE,
      useClass: BdHubspotTrackingService,
      multi: true,
    },
    {
      provide: TRACKING_SERVICE,
      useClass: BdDash0TrackingService,
      multi: true,
    },
    // `BdUserErrorHandler` is `providedIn: 'root'`, but consumers inject the
    // `ErrorHandler` token — only this binding routes application errors to
    // trackException → Dash0 (BDA-5426).
    { provide: ErrorHandler, useClass: BdUserErrorHandler },
    // PrimeNG does not mark `DialogService` `providedIn: 'root'`; components that
    // inject it without a local `providers: [BD_DIALOG_SERVICE_PROVIDER]` resolve it here.
    BD_DIALOG_SERVICE_PROVIDER,
    provideEnvironmentInitializer(() => {
      const zendeskService = inject(BdUserZendeskService);
      const pageTitleService = inject(BdPageTitleService);
      const currentUserService = inject(BdCurrentUserService);
      const versionCheckService = inject(BdVersionCheckService);

      pageTitleService.init();
      currentUserService.init();
      zendeskService.init();
      versionCheckService.init();
    }),
  ];
}
